/**
 * Which frames are the agent's mapped OUTPUT, and how to spot one being created.
 *
 * Extracted from WorkingPanel so the websocket hook can use the same rule. Two copies of "what counts as an
 * output frame" would eventually disagree, and the disagreement would show up as the panel either jumping
 * to Map & review for a working frame or refusing to jump for a real output.
 */

// The convention the agent is told to follow: name the mapped frame "<something>_output". Matched loosely
// (case-insensitive, anywhere in the name) because the agent renames it as the session goes on —
// "mapped_output" becomes "fixed_assets_output" when it moves to the next mapping.
export const isOutputKey = (key) => /_output/i.test(String(key ?? ""));

/**
 * The frame name in a `create_dataframe` result, or null if that is not what this output is.
 *
 * Parses the tool's own success message — `create_dataframe` returns
 * "Created DataFrame 'mapped_output' (8 rows x 8 columns) by cloning 'sales'." — because `tool_end` carries
 * only `{tool, output}` and no structured result (see callbacks.py::on_tool_end). Anchored at the start so
 * the trailing "by cloning '<source>'" cannot be mistaken for the new frame, which is the whole reason this
 * is a parser and not a substring test: cloning `sales_output` into `working` must NOT read as creating an
 * output frame.
 *
 * A test pins this against the exact string the tool returns, so changing that wording breaks the test
 * rather than silently disabling the jump.
 */
export function createdFrameName(output) {
  const match = /^Created DataFrame '([^']+)'/.exec(String(output ?? "").trim());
  return match ? match[1] : null;
}

/**
 * Did this `create_dataframe` result create the frame the user reviews?
 *
 * An error result returns false: a failed call must not pull the panel to an empty Map & review stage while
 * the agent is still recovering. Same reasoning as the charts board's success-only focus.
 */
export function createdAnOutputFrame(output) {
  const name = createdFrameName(output);
  return name !== null && isOutputKey(name);
}
